/**
 * ContextHelpButton.jsx
 *
 * Floating "?" button that appears on any screen.
 * - Pass `screenId` matching a HelpArticle's ScreenId field.
 * - Fetches the KB article for that screen; falls back to article list if none found.
 * - Opens a slide-in side panel (no modal overlay blocking work).
 *
 * Usage:
 *   <ContextHelpButton screenId="industry-matrix" screenLabel="Industry Module Matrix" />
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { HelpCircle, X, BookOpen, ExternalLink, ChevronRight, ArrowLeft } from 'lucide-react';
import { API } from '../lib/api';


const getToken = () =>
  sessionStorage.getItem('erp_token') || localStorage.getItem('erp_token');

// ── Simple markdown renderer (no deps) ────────────────────────────────────────
function renderMarkdown(text = '') {
  if (!text) return '';
  return text
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/^#{3}\s(.+)$/gm,  '<h4 style="margin:.8em 0 .3em;font-size:.9rem;font-weight:700;color:var(--text-primary)">$1</h4>')
    .replace(/^#{2}\s(.+)$/gm,  '<h3 style="margin:.9em 0 .4em;font-size:1rem;font-weight:700;color:var(--text-primary)">$1</h3>')
    .replace(/^#{1}\s(.+)$/gm,  '<h2 style="margin:1em 0 .5em;font-size:1.1rem;font-weight:700;color:var(--text-primary)">$1</h2>')
    .replace(/\*\*(.+?)\*\*/g,  '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g,      '<em>$1</em>')
    .replace(/`([^`]+)`/g,      '<code style="background:var(--surface-hover);padding:1px 5px;border-radius:4px;font-size:.85em">$1</code>')
    .replace(/^[-*]\s(.+)$/gm,  '<li style="margin:.2em 0">$1</li>')
    .replace(/(<li.*<\/li>\n?)+/g, m => `<ul style="margin:.4em 0 .4em 1.2em;padding:0">${m}</ul>`)
    .replace(/\n{2,}/g, '</p><p style="margin:.5em 0">')
    .replace(/\n/g, '<br/>');
}

export default function ContextHelpButton({ screenId = '', screenLabel = '' }) {
  const [open,       setOpen]       = useState(false);
  const [article,    setArticle]    = useState(null);
  const [allArticles,setAllArticles]= useState([]);
  const [loading,    setLoading]    = useState(false);
  const [showList,   setShowList]   = useState(false);
  const panelRef = useRef(null);

  const fetchArticle = useCallback(async () => {
    if (!screenId) { setShowList(true); return; }
    setLoading(true);
    try {
      const tok = getToken();
      const h   = tok ? { Authorization: `Bearer ${tok}` } : {};
      const r   = await fetch(`${API}/help-kb?screenId=${encodeURIComponent(screenId)}&published=true`, { headers: h });
      if (r.ok) {
        const d = await r.json();
        if (d.length > 0) { setArticle(d[0]); setShowList(false); }
        else { setShowList(true); fetchAllArticles(h); }
      } else { setShowList(true); fetchAllArticles(h); }
    } catch (_) { setShowList(true); }
    finally { setLoading(false); }
  }, [screenId]);

  const fetchAllArticles = async (h = {}) => {
    try {
      const tok = getToken();
      const hdr = h.Authorization ? h : tok ? { Authorization: `Bearer ${tok}` } : {};
      const r   = await fetch(`${API}/help-kb?published=true`, { headers: hdr });
      if (r.ok) setAllArticles(await r.json());
    } catch (_) {}
  };

  const handleOpen = () => {
    setOpen(true);
    fetchArticle();
  };

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = e => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  // Click-outside to close
  useEffect(() => {
    if (!open) return;
    const handler = e => {
      if (panelRef.current && !panelRef.current.contains(e.target) &&
          !e.target.closest('[data-help-btn]')) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <>
      {/* ── Floating trigger button ── */}
      <button
        data-help-btn
        data-testid={`help-btn-${screenId || 'global'}`}
        onClick={handleOpen}
        title={`Help: ${screenLabel || 'Help Center'}`}
        style={{
          position:     'fixed',
          bottom:        52,
          right:         16,
          zIndex:        1200,
          width:         36,
          height:        36,
          borderRadius:  '50%',
          border:       '1.5px solid var(--border)',
          background:   'var(--surface)',
          color:        'var(--accent-blue, #1890ff)',
          cursor:       'pointer',
          display:      'flex',
          alignItems:   'center',
          justifyContent:'center',
          boxShadow:    '0 2px 8px rgba(0,0,0,.18)',
          transition:   'transform .15s, box-shadow .15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.12)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,0,0,.28)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)';    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,.18)'; }}
      >
        <HelpCircle size={18} />
      </button>

      {/* ── Slide-in panel ── */}
      <div
        ref={panelRef}
        data-testid="help-panel"
        style={{
          position:   'fixed',
          top:         0,
          right:       open ? 0 : -420,
          bottom:      0,
          width:       400,
          zIndex:      1100,
          background: 'var(--surface)',
          borderLeft: '1px solid var(--border)',
          boxShadow:  '-4px 0 24px rgba(0,0,0,.18)',
          display:    'flex',
          flexDirection:'column',
          transition: 'right .22s cubic-bezier(.4,0,.2,1)',
          overflow:   'hidden',
        }}
      >
        {/* Panel header */}
        <div style={{ display:'flex', alignItems:'center', gap:8, padding:'12px 16px',
          borderBottom:'1px solid var(--border)', background:'var(--surface-hover)', flexShrink:0 }}>
          {showList && article && (
            <button onClick={() => setShowList(false)}
              style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', padding:4 }}>
              <ArrowLeft size={15} />
            </button>
          )}
          <BookOpen size={15} color="var(--accent-blue, #1890ff)" />
          <span style={{ fontWeight:700, fontSize:'.88rem', color:'var(--text-primary)', flex:1 }}>
            {showList ? 'Help Center' : (article?.title || screenLabel || 'Help')}
          </span>
          {!showList && (
            <button onClick={() => setShowList(true)} title="Browse all articles"
              style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', padding:4 }}>
              <BookOpen size={14} />
            </button>
          )}
          <button onClick={() => setOpen(false)} data-testid="help-panel-close"
            style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', padding:4 }}>
            <X size={16} />
          </button>
        </div>

        {/* Panel body */}
        <div style={{ flex:1, overflow:'auto', padding:'16px' }}>
          {loading && (
            <div style={{ textAlign:'center', color:'var(--text-muted)', marginTop:40, fontSize:'.85rem' }}>
              Loading help content…
            </div>
          )}

          {/* Article view */}
          {!loading && !showList && article && (
            <div>
              {article.category && (
                <span style={{ fontSize:'.7rem', fontWeight:600, padding:'2px 8px', borderRadius:20,
                  background:'var(--surface-hover)', color:'var(--text-muted)', marginBottom:10, display:'inline-block' }}>
                  {article.category}
                </span>
              )}
              {article.tags?.length > 0 && (
                <div style={{ display:'flex', flexWrap:'wrap', gap:4, marginBottom:10, marginTop:4 }}>
                  {article.tags.map(tag => (
                    <span key={tag} style={{ fontSize:'.68rem', padding:'2px 7px', borderRadius:10,
                      background:'rgba(24,144,255,.1)', color:'var(--accent-blue, #1890ff)' }}>
                      {tag}
                    </span>
                  ))}
                </div>
              )}
              <div style={{ fontSize:'.83rem', lineHeight:1.65, color:'var(--text-secondary)' }}
                dangerouslySetInnerHTML={{ __html: renderMarkdown(article.content) }} />
              <button onClick={() => setShowList(true)}
                style={{ marginTop:20, display:'flex', alignItems:'center', gap:4,
                  background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)',
                  fontSize:'.78rem', padding:0 }}>
                <ChevronRight size={12} /> Browse all articles
              </button>
            </div>
          )}

          {/* No article for this screen — fallback list */}
          {!loading && showList && (
            <div>
              {screenId && (
                <div style={{ fontSize:'.78rem', color:'var(--text-muted)', marginBottom:12,
                  padding:'8px 10px', background:'var(--surface-hover)', borderRadius:6 }}>
                  No specific article found for <strong>{screenLabel || screenId}</strong>.
                  Browse all available articles below:
                </div>
              )}
              {allArticles.length === 0 ? (
                <div style={{ textAlign:'center', color:'var(--text-muted)', marginTop:32, fontSize:'.83rem' }}>
                  No help articles published yet.
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                  {allArticles.map(a => (
                    <button
                      key={a.id}
                      onClick={() => { setArticle(a); setShowList(false); }}
                      data-testid={`help-article-item-${a.id}`}
                      style={{
                        display:'flex', alignItems:'flex-start', gap:10, padding:'10px 12px',
                        background:'var(--surface-hover)', border:'1px solid var(--border)',
                        borderRadius:8, cursor:'pointer', textAlign:'left',
                        transition:'background .12s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background='var(--border)'}
                      onMouseLeave={e => e.currentTarget.style.background='var(--surface-hover)'}
                    >
                      <BookOpen size={13} color="var(--accent-blue, #1890ff)" style={{ marginTop:2, flexShrink:0 }} />
                      <div>
                        <div style={{ fontSize:'.82rem', fontWeight:600, color:'var(--text-primary)', marginBottom:2 }}>
                          {a.title}
                        </div>
                        {a.screenLabel && (
                          <div style={{ fontSize:'.7rem', color:'var(--text-muted)' }}>{a.screenLabel}</div>
                        )}
                      </div>
                      <ExternalLink size={11} color="var(--text-muted)" style={{ marginLeft:'auto', flexShrink:0, marginTop:3 }} />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Backdrop (semi-transparent, only on mobile widths) ── */}
      {open && (
        <div onClick={() => setOpen(false)}
          style={{ position:'fixed', inset:0, zIndex:1050,
            background:'rgba(0,0,0,.12)', display:'none',
            // Show backdrop only on narrow screens
          }} />
      )}
    </>
  );
}
